import React, { useEffect, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { Button, Modal, Popconfirm, message, Tag, Form, Input, DatePicker } from "antd";
import {
  ArrowLeftOutlined,
  EditOutlined,
  DeleteOutlined,
  LeftOutlined,
  RightOutlined,
  PlusOutlined,
} from "@ant-design/icons";
import profile from "../../../Assests/Images/prodileDp.png";
import "./style.css";
import { eventAPI } from "../../../utils/api";
import { useRef } from "react";
import { ClipLoader } from "react-spinners";
import dayjs from "dayjs";
import GenericTable from "../../../Components/GenericTable";

const EventDetail = () => {
  const MEDIA_URL = process.env.REACT_APP_MEDIA_URL;
  const navigate = useNavigate();
  const location = useLocation();
  const eventId = location?.state?.eventId;
  const [messageApi, contextHolder] = message.useMessage();
  const [eventDetails, setEventDetails] = useState({});
  const printRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [btnLoading, setBtnLoading] = useState(false);
  const [btnLoadingSend, setBtnLoadingSend] = useState(false);
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);
  const [payments, setPayments] = useState([]);
  const [paymentModalOpen, setPaymentModalOpen] = useState(false);
  const [editingPayment, setEditingPayment] = useState(null);
  const [paymentSaving, setPaymentSaving] = useState(false);
  const [paymentForm] = Form.useForm();

  const openLightbox = (index) => {
    setCurrentImageIndex(index);
    setLightboxOpen(true);
  };

  const goToPrev = () => {
    setCurrentImageIndex((prev) =>
      prev === 0 ? eventDetails.reference_images.length - 1 : prev - 1
    );
  };

  const goToNext = () => {
    setCurrentImageIndex((prev) =>
      prev === eventDetails.reference_images.length - 1 ? 0 : prev + 1
    );
  };

  const formatTime12h = (time) => {
    if (!time) return "-";
    return dayjs(time, "HH:mm:ss").format("hh:mm A");
  };

  const loadEventDetails = async () => {
    try {
      const response = await eventAPI.getEventDetail(eventId);
      setEventDetails(response?.data);
    } catch (error) {
      messageApi.error("Failed to load event details");
    }
  };

  const loadPayments = async () => {
    try {
      const response = await eventAPI.getPayments(eventId);
      setPayments(response?.data || []);
    } catch (error) {
      messageApi.error("Failed to load payment history");
    }
  };

  // Load event + payment data
  useEffect(() => {
    if (eventId) {
      (async () => {
        setLoading(true);
        await Promise.all([loadEventDetails(), loadPayments()]);
        setLoading(false);
      })();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eventId]);

  const refreshAfterPaymentChange = async () => {
    await Promise.all([loadEventDetails(), loadPayments()]);
  };

  const openAddPayment = () => {
    setEditingPayment(null);
    paymentForm.resetFields();
    paymentForm.setFieldsValue({ paid_on: dayjs() });
    setPaymentModalOpen(true);
  };

  const openEditPayment = (payment) => {
    setEditingPayment(payment);
    paymentForm.setFieldsValue({
      amount: payment.amount,
      paid_on: dayjs(payment.paid_on, "YYYY-MM-DD"),
      method: payment.method,
      note: payment.note,
    });
    setPaymentModalOpen(true);
  };

  const handlePaymentSubmit = async (values) => {
    try {
      setPaymentSaving(true);
      const payload = {
        amount: parseFloat(values.amount) || 0,
        paid_on: values.paid_on.format("YYYY-MM-DD"),
        method: values.method || "",
        note: values.note || "",
      };

      if (editingPayment) {
        await eventAPI.updatePayment(eventId, editingPayment.id, payload);
        messageApi.success("Payment updated");
      } else {
        await eventAPI.addPayment(eventId, payload);
        messageApi.success("Payment recorded");
      }

      setPaymentModalOpen(false);
      await refreshAfterPaymentChange();
    } catch (error) {
      messageApi.error(error.message || "Failed to save payment");
    } finally {
      setPaymentSaving(false);
    }
  };

  const handleDeletePayment = async (payment) => {
    try {
      await eventAPI.deletePayment(eventId, payment.id);
      messageApi.success("Payment removed");
      await refreshAfterPaymentChange();
    } catch (error) {
      messageApi.error(error.message || "Failed to remove payment");
    }
  };

  const handleDownloadDetail = async () => {
    try {
      setBtnLoading(true);

      const blob = await eventAPI.getEventPDF(eventId);

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");

      const safeClientName = (eventDetails?.client_name || "Client").replace(
        /[\\/:*?"<>|]/g,
        ""
      );
      link.href = url;
      link.download = `${safeClientName} - ${eventDetails?.start_date}.pdf`;
      document.body.appendChild(link);
      link.click();

      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error("Download failed", err);
    } finally {
      setBtnLoading(false);
    }
  };

  const handleSendToClient = async () => {
    try {
      setBtnLoadingSend(true);

      const formData = new FormData();
      formData.append("event_id", eventId);

      // Send to backend
      await eventAPI.sendEventPDF(formData);

      messageApi.success("PDF sent to client successfully!");
    } catch (error) {
      console.error(error);
      messageApi.error("Failed to send PDF to client.");
    } finally {
      setBtnLoadingSend(false);
    }
  };

  const handleEdit = () => {
    navigate("/add-new-event", { state: { eventId: eventId } });
  };

  const handleDelete = async () => {
    try {
      await eventAPI.deleteEvent(eventId);
      messageApi.success(`Event deleted successfully`);
      navigate("/event");
    } catch (error) {
      messageApi.error(error.message || "Failed to delete event");
    }
  };

  const capitalizeFirst = (s) => (s ? s[0].toUpperCase() + s.slice(1) : "");

  return loading ? (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
      }}
      className="pdf-loader-overlay"
    >
      <ClipLoader size={60} color="#e2ae03" />
    </div>
  ) : (
    <div className="view-event">
      {contextHolder}
      <div className="back-btn">
        <Button
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate(-1)}
          className="back-btn"
        >
          Back
        </Button>
      </div>

      <div ref={printRef} className="pdf-content">
        <div className="profile-details">
          <div className="image">
            <img width={88} src={profile} alt="Profile Icon" />
          </div>

          <div className="profile-wrapper">
            <div className="info">
              <div className="name">{eventDetails?.client_name}</div>
              <div className="email">{eventDetails?.client_email}</div>
              <Tag
                color={
                  eventDetails?.status === "cancel"
                    ? "default"
                    : eventDetails?.deposit_met
                    ? "success"
                    : "warning"
                }
                style={{ marginTop: 6 }}
              >
                {eventDetails?.status === "cancel"
                  ? "Cancelled"
                  : eventDetails?.deposit_met
                  ? "Booking Confirmed"
                  : "Booking Not Confirmed"}
              </Tag>
            </div>
            <div className="btn-wrappers">
              <div className="edit-btn">
                <Popconfirm
                  title="Delete this item?"
                  description="This action cannot be undone."
                  okText="Yes"
                  cancelText="No"
                  onConfirm={handleDelete}
                >
                  <Button danger icon={<DeleteOutlined />}>
                    Delete
                  </Button>
                </Popconfirm>
              </div>
              <div className="edit-btn">
                <Button
                  type="button"
                  className="edit-button"
                  onClick={handleEdit}
                  icon={<EditOutlined />}
                >
                  Edit
                </Button>
              </div>
            </div>
          </div>
        </div>
        <div className="event-info">
          <div className="heading">Event Information</div>
          <div className="event-summary">
            <div className="summary-row">
              <div className="summary-item">
                <span className="label">Events Category</span>
                <span className="value">
                  {capitalizeFirst(eventDetails.category)}
                </span>
              </div>

              <div className="summary-item">
                <span className="label">Venue Address</span>
                <span className="value">
                  {capitalizeFirst(eventDetails.address)}
                </span>
              </div>
            </div>

            <div className="summary-row">
              <div className="summary-item">
                <span className="label">Event Name</span>
                <span className="value">{eventDetails?.name}</span>
              </div>

              <div className="summary-item">
                <span className="label">Event Time</span>
                <span className="value">
                  {formatTime12h(eventDetails?.event_time)}
                </span>
              </div>
            </div>

            <div className="summary-row">
              <div className="summary-item">
                <span className="label">Event Start Date</span>
                <span className="value">{eventDetails?.start_date}</span>
              </div>

              <div className="summary-item">
                <span className="label">Event End Date</span>
                <span className="value">{eventDetails?.end_date}</span>
              </div>
            </div>
          </div>
        </div>
        <div className="buffer-time">
          <div className="heading">Buffer Time</div>
          <div className="summary-row">
            <div className="summary-item">
              <span className="label">Setup Start Time</span>
              <span className="value">
                {formatTime12h(eventDetails?.setup_start_time)}
              </span>
            </div>

            <div className="summary-item">
              <span className="label">Teardown Time</span>
              <span className="value">
                {formatTime12h(eventDetails?.teardown_time)}
              </span>
            </div>
          </div>
        </div>
        <div className="gold-package">
          <div className="package">
            <div className="heading">Package</div>
            <div className="sub-heading">
              {eventDetails?.package_details?.name}
            </div>

            <div className="heading">Package Description</div>
            <div
              style={{ fontSize: "14px", fontWeight: 300, color: "gray" }}
              className="sub-heading"
            >
              {eventDetails?.package_details?.description}
            </div>
          </div>
          <div className="refer-image">
            <div className="heading">References Images</div>
            <div className="refer-images">
              {eventDetails?.reference_images?.length > 0 ? (
                eventDetails.reference_images.map((item, index) => (
                  <img
                    style={{
                      width: "70px",
                      height: "60px",
                      borderRadius: "14px",
                      cursor: "pointer",
                    }}
                    key={item.id}
                    src={`${MEDIA_URL}${item.image}`}
                    alt="Reference"
                    onClick={() => openLightbox(index)}
                  />
                ))
              ) : (
                <span className="no-images">No reference images</span>
              )}
            </div>
          </div>
        </div>
        <div className="package-addons">
          <div className="section-title">Package Add On Details</div>

          <div className="addons-list">
            {eventDetails?.addons?.map((item, index) => (
              <div className="addon-item" key={index}>
                <span className="custom-check">
                  <span className="inner-circle">✓</span>
                </span>
                <span>{item?.name}</span>
              </div>
            ))}
          </div>
        </div>
        <div className="client-notes">
          <div className="heading ">Client Notes</div>
          <div className="content">
            {(eventDetails?.description ?? "")
              .replace(/\r\n/g, "\n")
              .replace(/\r/g, "\n")}
          </div>
        </div>
        <div className="invoice-amount">
          <div className="heading">Invoice Amount</div>

          {!eventDetails?.deposit_met && eventDetails?.status !== "cancel" && (
            <div
              style={{
                fontSize: 12,
                color: "#666",
                background: "#f7fbff",
                border: "1px solid #d4d7e3",
                borderRadius: 10,
                padding: "10px 12px",
                marginBottom: 12,
              }}
            >
              A deposit of at least 25% of the event value is required to confirm a booking, and
              the remaining balance must be fully cleared one week before the event date.
            </div>
          )}

          <div className="summary-row" style={{ gap: "8px" }}>
            {!eventDetails?.deposit_met && eventDetails?.status !== "cancel" && (
              <Tag color="warning">Booking Not Confirm – Deposit Pending</Tag>
            )}
            {eventDetails?.balance_due_warning && (
              <Tag color="error">Balance Due Soon</Tag>
            )}
          </div>

          <div className="summary-row">
            <div className="summary-item">
              <span className="label">Event Amount</span>
              <span className="value">$ {eventDetails?.value}</span>
            </div>

            <div className="summary-item">
              <span className="label">Balance Due</span>
              <span className="value">$ {eventDetails?.balance_due ?? 0}</span>
            </div>
          </div>

          <div className="summary-row">
            <div className="summary-item">
              <span className="label">Advance Payment</span>
              <span className="value">
                {eventDetails?.advance_payment ? "Yes" : "No"}
              </span>
            </div>

            {eventDetails?.advance_payment && (
              <div className="summary-item">
                <span className="label">Total Paid So Far</span>
                <span className="value">
                  $ {eventDetails?.advance_payment_amount || 0}
                </span>
              </div>
            )}
          </div>

          <div className="summary-row">
            <div className="summary-item">
              <span className="label">Remaining Amount Cleared</span>
              <span className="value">
                {eventDetails?.remaining_amount_cleared ? "Yes" : "No"}
              </span>
            </div>
          </div>
        </div>

        <div className="payment-history">
          <div
            className="heading"
            style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}
          >
            Payment History
            <Button
              size="small"
              icon={<PlusOutlined />}
              onClick={openAddPayment}
            >
              Add Payment
            </Button>
          </div>

          <GenericTable
            title=""
            loading={false}
            onRowClick={() => {}}
            dataSource={payments.map((p) => ({ ...p, key: p.id }))}
            columns={[
              { title: "Date", dataIndex: "paid_on", key: "paid_on" },
              {
                title: "Amount",
                dataIndex: "amount",
                key: "amount",
                render: (amount) => `$ ${amount}`,
              },
              { title: "Method", dataIndex: "method", key: "method" },
              { title: "Note", dataIndex: "note", key: "note" },
              {
                title: "Actions",
                key: "actions",
                render: (_, record) => (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{ display: "flex", gap: "8px" }}
                  >
                    <Button
                      size="small"
                      icon={<EditOutlined />}
                      onClick={() => openEditPayment(record)}
                    />
                    <Popconfirm
                      title="Remove this payment?"
                      okText="Yes"
                      cancelText="No"
                      onConfirm={() => handleDeletePayment(record)}
                    >
                      <Button size="small" danger icon={<DeleteOutlined />} />
                    </Popconfirm>
                  </div>
                ),
              },
            ]}
          />
        </div>
      </div>

      <Modal
        title={editingPayment ? "Edit Payment" : "Add Payment"}
        open={paymentModalOpen}
        onCancel={() => setPaymentModalOpen(false)}
        onOk={() => paymentForm.submit()}
        confirmLoading={paymentSaving}
        okText="Save"
      >
        <Form form={paymentForm} layout="vertical" onFinish={handlePaymentSubmit}>
          <Form.Item
            name="amount"
            label="Amount"
            rules={[
              { required: true, message: "Please enter the amount" },
              {
                validator: (_, value) =>
                  value > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error("Amount must be greater than 0")),
              },
            ]}
          >
            <Input type="number" min={0.01} step={0.01} placeholder="0.00" />
          </Form.Item>
          <Form.Item
            name="paid_on"
            label="Date Paid"
            rules={[{ required: true, message: "Please select the date" }]}
          >
            <DatePicker style={{ width: "100%" }} format="YYYY-MM-DD" />
          </Form.Item>
          <Form.Item name="method" label="Method">
            <Input placeholder="Cash, e-transfer, card..." />
          </Form.Item>
          <Form.Item name="note" label="Note">
            <Input.TextArea autoSize={{ minRows: 2, maxRows: 4 }} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        open={lightboxOpen}
        onCancel={() => setLightboxOpen(false)}
        footer={null}
        centered
        width="auto"
        className="lightbox-modal"
        styles={{ body: { padding: 0 } }}
      >
        <div className="lightbox-container">
          {eventDetails?.reference_images?.length > 1 && (
            <button className="lightbox-nav lightbox-prev" onClick={goToPrev}>
              <LeftOutlined />
            </button>
          )}
          <img
            src={`${MEDIA_URL}${eventDetails?.reference_images?.[currentImageIndex]?.image}`}
            alt="Reference enlarged"
            className="lightbox-image"
          />
          {eventDetails?.reference_images?.length > 1 && (
            <button className="lightbox-nav lightbox-next" onClick={goToNext}>
              <RightOutlined />
            </button>
          )}
          {eventDetails?.reference_images?.length > 1 && (
            <div className="lightbox-counter">
              {currentImageIndex + 1} / {eventDetails.reference_images.length}
            </div>
          )}
        </div>
      </Modal>

      <div className="submit-btns">
        <Button
          type="button"
          onClick={handleDownloadDetail}
          className="submit-btn"
          loading={btnLoading}
        >
          Download Detail (PDF)
        </Button>
        <Button
          type="button"
          onClick={handleSendToClient}
          className="submit-btn"
          loading={btnLoadingSend}
        >
          Send To Client
        </Button>
      </div>
    </div>
  );
};

export default EventDetail;
